import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { dataService } from '@/services/data-service';
import type { AuditLog, AuditLogFilter } from '@/types/audit';
import type { Asset } from '@/types/asset';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  FileText, 
  Filter, 
  ChevronLeft, 
  ChevronRight,
  Loader2,
  Search,
  FileSpreadsheet,
  Mail,
  Zap,
  Columns,
  FileX,
  ArrowUp,
  ArrowDown,
  ArrowRight
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { formatFieldName, formatActionType } from '@/lib/format-utils';
import { useColumnConfig } from '@/hooks/use-column-config';
import { DEFAULT_AUDIT_COLUMNS, AUDIT_COLUMNS_STORAGE_KEY } from '@/lib/audit-columns-config';
import { exportAuditLogsToPDF, downloadPDF, getPDFBlob } from '@/lib/pdf-export';
import { EmailDialog, type EmailData } from '@/components/email-dialog';
import { AssetEditor } from '@/components/assets/asset-editor';
import { PageWithRightPanel } from '@/components/layout/PageWithRightPanel';
import { RightPanelBase, type RightPanelTab } from '@/components/RightPanel/RightPanelBase';
import { useRightPanel } from '@/contexts/RightPanelContext';
import { useColumnResize } from '@/hooks/use-column-resize';
import { AuditColumnsTab } from '@/components/RightPanel/AuditColumnsTab';
import { AuditTrailFiltersTab } from '@/components/RightPanel/AuditTrailFiltersTab';
import { ProductTour } from '@/components/ui/product-tour';
import { useTour } from '@/contexts/TourContext';
import { useAuditTrailTour } from '@/hooks/use-audit-trail-tour';

interface SortIconProps {
  readonly columnKey: keyof AuditLog;
  readonly sortConfig: { key: keyof AuditLog | null; direction: 'asc' | 'desc' | null };
}

function SortIcon({ columnKey, sortConfig }: SortIconProps) {
  if (sortConfig.key !== columnKey) {
    return null;
  }
  return sortConfig.direction === 'asc' ? (
    <ArrowUp className="ml-2 h-4 w-4" />
  ) : (
    <ArrowDown className="ml-2 h-4 w-4" />
  );
}

// Helper function to simulate sending email - extracted to reduce nesting depth (S2004)
function simulateEmailSend(): Promise<boolean> {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(true);
    }, 1000);
  });
}

// Helper function to apply sort direction - extracted to reduce cognitive complexity
function applySortDirection(diff: number, direction: 'asc' | 'desc'): number {
  return direction === 'asc' ? diff : -diff;
}

// Helper function to compare dates - extracted to reduce cognitive complexity
function compareDates(aStr: string, bStr: string, direction: 'asc' | 'desc'): number | null {
  const aDate = new Date(aStr);
  const bDate = new Date(bStr);
  if (isNaN(aDate.getTime()) || isNaN(bDate.getTime())) return null;
  return applySortDirection(aDate.getTime() - bDate.getTime(), direction);
}

// Helper function to compare audit log values for sorting - refactored to reduce cognitive complexity (S3776)
function compareAuditLogValues(aValue: any, bValue: any, direction: 'asc' | 'desc'): number {
  if (aValue == null && bValue == null) return 0;
  if (aValue == null) return 1;
  if (bValue == null) return -1;

  if (typeof aValue === 'string' && typeof bValue === 'string') {
    const dateComparison = compareDates(aValue, bValue, direction);
    if (dateComparison !== null) return dateComparison;
    return applySortDirection(aValue.localeCompare(bValue), direction);
  }

  if (typeof aValue === 'number' && typeof bValue === 'number') {
    return applySortDirection(aValue - bValue, direction);
  }

  return 0;
}

export default function AuditPage(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [, setActionTypes] = useState<string[]>([]);
  const [, setUsers] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

  // Right Panel Context
  const {
    setIsExpanded,
    setActiveTab,
    selectedRowIds,
    setSelectedRowIds,
    isPinned,
    appliedFilters,
    isApplyingFilters,
    setPendingFilters,
  } = useRightPanel();

  // Product Tour
  const { setOnStartTour } = useTour();
  const { isTourOpen, tourSteps, startTour, closeTour, completeTour } = useAuditTrailTour();

  useEffect(() => {
    setOnStartTour(() => startTour);
    return () => setOnStartTour(null);
  }, [setOnStartTour, startTour]);

  // Column configuration with persistence
  const {
    visibleColumns: configVisibleColumns,
  } = useColumnConfig({
    storageKey: AUDIT_COLUMNS_STORAGE_KEY,
    defaultColumns: DEFAULT_AUDIT_COLUMNS,
  });

  // Build ordered visible columns for rendering
  const visibleColumns = configVisibleColumns.filter(col => col.id !== 'select');
  
  // Filter state - now synced with RightPanelContext
  const [filters, setFilters] = useState<AuditLogFilter>({
    assetCode: searchParams.get('assetCode') ?? undefined,
    assetDescription: searchParams.get('assetDescription') ?? undefined,
    startDate: searchParams.get('startDate') ?? undefined,
    endDate: searchParams.get('endDate') ?? undefined,
    actionType: searchParams.get('actionType') ?? undefined,
    user: searchParams.get('user') ?? undefined,
    page: parseInt(searchParams.get('page') ?? '1'),
    pageSize: 25,
    sortBy: 'Timestamp',
    sortOrder: 'desc'
  });

// Sync filters with appliedFilters from RightPanelContext
  useEffect(() => {
    if (!appliedFilters) return;
    
    const newFilters: AuditLogFilter = {
      assetCode: appliedFilters.auditTrailAssetCode,
      assetDescription: appliedFilters.auditTrailAssetDescription,
      startDate: appliedFilters.auditTrailDateRange?.from?.toISOString().split('T')[0],
      endDate: appliedFilters.auditTrailDateRange?.to?.toISOString().split('T')[0],
      actionType: appliedFilters.auditTrailActionTypes?.[0],
      user: appliedFilters.auditTrailUsers?.[0],
      page: filters.page,
      pageSize: filters.pageSize,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder
    };
    setFilters(newFilters);
  }, [appliedFilters]);

  // Set pending filters from URL params on initial load so the filter UI reflects deep link filters
  useEffect(() => {
    const assetCodeParam = searchParams.get('assetCode');
    const assetDescriptionParam = searchParams.get('assetDescription');
    const startDateParam = searchParams.get('startDate');
    const endDateParam = searchParams.get('endDate');
    const actionTypeParam = searchParams.get('actionType');
    const userParam = searchParams.get('user');
    
    // Only set pending filters if there are URL params
    if (assetCodeParam || assetDescriptionParam || startDateParam || endDateParam || actionTypeParam || userParam) {
      setPendingFilters({
        selectedCategories: [],
        selectedStatuses: [],
        selectedLocations: [],
        acquisitionDateRange: { from: undefined, to: undefined },
        acquisitionCostMin: '',
        acquisitionCostMax: '',
        netBookValueMin: '',
        netBookValueMax: '',
        selectedAssignedTo: [],
        selectedDepreciationMethods: [],
        warrantyExpiryDateRange: { from: undefined, to: undefined },
        selectedManufacturers: [],
        // Audit trail specific filters
        auditTrailAssetCode: assetCodeParam ?? undefined,
        auditTrailAssetDescription: assetDescriptionParam ?? undefined,
        auditTrailDateRange: {
          from: startDateParam ? new Date(startDateParam) : undefined,
          to: endDateParam ? new Date(endDateParam) : undefined,
        },
        auditTrailActionTypes: actionTypeParam ? [actionTypeParam] : [],
        auditTrailUsers: userParam ? [userParam] : [],
      });
    }
  }, [searchParams, setPendingFilters]);

  // Sort configuration
  const [sortConfig, setSortConfig] = useState<{
    key: keyof AuditLog | null;
    direction: 'asc' | 'desc' | null;
  }>({
    key: 'timestamp',
    direction: 'desc',
  });

  // Column resize hook
  const initialColumnWidths = {
    select: 80,
    timestamp: 180,
    assetCode: 150,
    assetName: 300,
    user: 150,
    actionType: 150,
    field: 180,
    changes: 400,
  };

  const { columnWidths, handleMouseDown } = useColumnResize(initialColumnWidths, ['select']);

  useEffect(() => {
    loadAuditLogs();
    loadActionTypes();
    loadUsers();
  }, [filters]);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      const result = await dataService.getAuditLogs(filters);
      setAuditLogs(result.items);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const loadActionTypes = async () => {
    try {
      const types = await dataService.getAuditActionTypes();
      setActionTypes(types);
    } catch (error) {
      console.error('Failed to load action types:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const userList = await dataService.getAuditUsers();
      setUsers(userList);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };



  const handleExport = async (format: 'csv' | 'pdf' | 'excel') => {
    try {
      if (format === 'pdf') {
        handleExportPDF();
      } else {
        console.log(`Exporting to ${format.toUpperCase()}...`);
        toast.success(`Export to ${format.toUpperCase()} started`);
        // TODO: Implement CSV and Excel export functionality
      }
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export audit trail');
    }
  };

  const handleExportPDF = () => {
    try {
      // Get visible column IDs
      const visibleColumnIds = visibleColumns.map(col => col.id);
      
      // Generate PDF
      const doc = exportAuditLogsToPDF({
        title: 'Audit Trail Report',
        data: filteredAuditLogs,
        visibleColumns: visibleColumnIds,
        filters: {
          assetCode: filters.assetCode,
          assetDescription: filters.assetDescription,
          startDate: filters.startDate,
          endDate: filters.endDate,
          actionType: filters.actionType,
          user: filters.user,
        },
      });

      // Download PDF
      const filename = `audit-trail-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.pdf`;
      downloadPDF(doc, filename);
      
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error('Failed to export PDF');
    }
  };

  const handleEmailPDF = async () => {
    try {
      // Get visible column IDs
      const visibleColumnIds = visibleColumns.map(col => col.id);
      
      // Generate PDF
      const doc = exportAuditLogsToPDF({
        title: 'Audit Trail Report',
        data: filteredAuditLogs,
        visibleColumns: visibleColumnIds,
        filters: {
          assetCode: filters.assetCode,
          assetDescription: filters.assetDescription,
          startDate: filters.startDate,
          endDate: filters.endDate,
          actionType: filters.actionType,
          user: filters.user,
        },
      });

      // Get PDF blob
      const blob = getPDFBlob(doc);
      setPdfBlob(blob);
      
      // Open email dialog
      setEmailDialogOpen(true);
    } catch (error) {
      console.error('Failed to prepare PDF for email:', error);
      toast.error('Failed to prepare PDF for email');
    }
  };

  const handleSendEmail = async (emailData: EmailData) => {
    try {
      if (!pdfBlob) {
        throw new Error('PDF not generated');
      }

      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(pdfBlob);
      
      await new Promise((resolve, reject) => {
        reader.onloadend = async () => {
          const base64data = reader.result as string;
          
          // TODO: Call API to send email with PDF attachment
          console.log('Sending email with PDF:', {
            ...emailData,
            attachment: base64data.substring(0, 100) + '...',
          });
          
          // Simulate API call using extracted helper function
          await simulateEmailSend();
          resolve(true);
        };
        reader.onerror = reject;
      });

      toast.success('Email sent successfully');
    } catch (error) {
      console.error('Failed to send email:', error);
      throw error;
    }
  };

  const handleAssetClose = () => {
    setSelectedAsset(null);
  };

  const handleAssetSave = async (updatedAsset: Asset) => {
    // In view mode from audit, we don't allow saving
    toast.info('Asset details are read-only from audit view');
  };

  const getActionTypeBadgeStyle = (actionType: string) => {
    const normalizedType = (actionType ?? '').toLowerCase().replace(/\s+/g, '');
    
    // Subtle pastel colors for light mode
    switch (normalizedType) {
      case 'create':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'edit':
      case 'update':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'delete':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'move':
      case 'transfer':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'statuschange':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      case 'valuechange':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      default:
        return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
    }
  };

  // Filter audit logs by search query
  const filteredAuditLogs = auditLogs.filter((log) => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      log.assetCode?.toLowerCase().includes(query) ||
      log.assetName?.toLowerCase().includes(query) ||
      log.user?.toLowerCase().includes(query) ||
      log.actionType?.toLowerCase().includes(query) ||
      log.field?.toLowerCase().includes(query) ||
      log.oldValue?.toLowerCase().includes(query) ||
      log.newValue?.toLowerCase().includes(query)
    );
  });

  // Sort handler - cycles through: unsorted → asc → desc → unsorted
  const handleSort = (key: keyof AuditLog) => {
    setSortConfig((prevConfig) => {
      // If clicking a different column, start with asc
      if (prevConfig.key !== key) {
        return { key, direction: 'asc' };
      }
      // If same column, cycle through: asc → desc → unsorted
      if (prevConfig.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      if (prevConfig.direction === 'desc') {
        return { key: null, direction: null };
      }
      // If unsorted, start with asc
      return { key, direction: 'asc' };
    });
  };

  // Apply sorting to filtered audit logs - using extracted helper function to reduce cognitive complexity (S3776)
  const sortedAuditLogs = [...filteredAuditLogs].sort((a: any, b: any) => {
    if (!sortConfig.key || !sortConfig.direction) return 0;
    
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    return compareAuditLogValues(aValue, bValue, sortConfig.direction);
  });

  // Pagination calculations
  const pageSize = 25;
  const totalPagesCalc = Math.ceil(sortedAuditLogs.length / pageSize);
  const startIndex = (filters.page! - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedAuditLogs = sortedAuditLogs.slice(startIndex, endIndex);

  // Reset to page 1 when filters or search changes
  useEffect(() => {
    if (filters.page !== 1) {
      setFilters({ ...filters, page: 1 });
    }
  }, [searchQuery]);

  // Row selection handler
  const handleSelectionChange = useCallback((rowId: string, checked: boolean) => {
    setSelectedRowIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(rowId);
        if (newSet.size === 1 && !isPinned) {
          setIsExpanded(true);
          setActiveTab('actions');
        }
      } else {
        newSet.delete(rowId);
        if (newSet.size === 0 && !isPinned) {
          setIsExpanded(false);
        }
      }
      return newSet;
    });
  }, [setIsExpanded, setActiveTab, setSelectedRowIds, isPinned]);

  // Select all handler
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      const allIds = new Set(paginatedAuditLogs.map(log => log.id));
      setSelectedRowIds(allIds);
      if (allIds.size > 0 && !isPinned) {
        setIsExpanded(true);
        setActiveTab('actions');
      }
    } else {
      setSelectedRowIds(new Set());
      if (!isPinned) {
        setIsExpanded(false);
      }
    }
  }, [paginatedAuditLogs, setSelectedRowIds, setIsExpanded, setActiveTab, isPinned]);

  // Helper function to get sort key for a column
  const getSortKeyForColumn = (columnId: string): keyof AuditLog | null => {
    const sortKeyMap: Record<string, keyof AuditLog> = {
      timestamp: 'timestamp',
      assetCode: 'assetCode',
      assetName: 'assetName',
      user: 'user',
      actionType: 'actionType',
      field: 'field',
    };
    return sortKeyMap[columnId] ?? null;
  };

  // Helper function to render table cell content
  const renderCellContent = (columnId: string, log: AuditLog) => {
    switch (columnId) {
      case 'timestamp':
        return (
          <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 font-mono text-sm text-slate-700 dark:text-slate-300 tabular-nums">
            {format(new Date(log.timestamp), 'dd/MM/yyyy HH:mm:ss')}
          </TableCell>
        );
      case 'assetCode':
        return (
          <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3">
            <button
              onClick={() => navigate(`/assets/${log.assetId}`)}
              className="text-primary hover:text-primary/80 font-medium hover:underline hover:underline-offset-4 transition-all cursor-pointer inline-flex items-center gap-1 group"
            >
              {log.assetCode}
              <span className="opacity-0 group-hover:opacity-100 transition-opacity">→</span>
            </button>
          </TableCell>
        );
      case 'assetName':
        return (
          <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3">
            <button
              onClick={() => navigate(`/assets/${log.assetId}`)}
              className="text-primary hover:text-primary/80 font-medium hover:underline hover:underline-offset-4 transition-all cursor-pointer inline-flex items-center gap-1 group"
            >
              {log.assetName}
              <span className="opacity-0 group-hover:opacity-100 transition-opacity">→</span>
            </button>
          </TableCell>
        );
      case 'user':
        return (
          <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300">
            {log.user}
          </TableCell>
        );
      case 'actionType':
        return (
          <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3">
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getActionTypeBadgeStyle(log.actionType)}`} data-tour="action-type-badge">
              {formatActionType(log.actionType)}
            </span>
          </TableCell>
        );
      case 'field':
        return (
          <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300">
            {formatFieldName(log.field ?? '')}
          </TableCell>
        );
      case 'changes':
        return (
          <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 max-w-xs">
            {log.oldValue && log.newValue ? (
              <span className="text-sm">
                <span className="text-red-600 dark:text-red-400">{log.oldValue}</span>
                {' → '}
                <span className="text-green-600 dark:text-green-400">{log.newValue}</span>
              </span>
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </TableCell>
        );
      default:
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="px-4 py-3 text-slate-700 dark:text-slate-300">-</TableCell>;
    }
  };

  const activeFilterCount = appliedFilters ? [
    appliedFilters.auditTrailAssetCode,
    appliedFilters.auditTrailAssetDescription,
    appliedFilters.auditTrailDateRange?.from,
    appliedFilters.auditTrailDateRange?.to,
    appliedFilters.auditTrailActionTypes?.length,
    appliedFilters.auditTrailUsers?.length
  ].filter(Boolean).length : 0;

  // Action Card Component
  const ActionCard = ({
    icon: Icon,
    title,
    description,
    onClick,
    variant,
    disabled
  }: {
    icon: React.ElementType;
    title: string;
    description: string;
    onClick?: () => void;
    variant: 'view' | 'action' | 'delete';
    disabled?: boolean;
  }) => {
    const variantStyles = {
      view: {
        container: 'bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 dark:hover:bg-blue-950/50',
        icon: 'text-blue-600 dark:text-blue-400',
        iconBg: 'bg-blue-100 dark:bg-blue-900/50'
      },
      action: {
        container: 'bg-primary/5 dark:bg-primary/10 hover:bg-primary/10 dark:hover:bg-primary/15',
        icon: 'text-primary dark:text-primary',
        iconBg: 'bg-primary/10 dark:bg-primary/20'
      },
      delete: {
        container: 'bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50',
        icon: 'text-red-600 dark:text-red-400',
        iconBg: 'bg-red-100 dark:bg-red-900/50'
      }
    };

    const styles = variantStyles[variant];

    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'w-full p-[5px] rounded-lg transition-all duration-200 text-left group cursor-pointer',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          styles.container
        )}
      >
        <div className="flex items-center gap-3">
          <div className={cn(
            'flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110',
            styles.iconBg
          )}>
            <Icon className={cn('h-6 w-6', styles.icon)} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-semibold text-sm text-foreground">
                {title}
              </h3>
              <ArrowRight className={cn('h-4 w-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity', styles.icon)} />
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
              {description}
            </p>
          </div>
        </div>
      </button>
    );
  };

  // Custom Actions Tab
  const CustomActionsTab = () => {
    const hasSelection = selectedRowIds.size > 0;

    return (
      <div className="p-4 space-y-3">
        {/* Export All Section - Always visible */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
            Export
          </h4>
          <ActionCard
            icon={FileText}
            title="Export to PDF"
            description="Generate PDF report of all audit logs"
            variant="action"
            onClick={() => handleExport('pdf')}
          />
          <ActionCard
            icon={FileSpreadsheet}
            title="Export to CSV"
            description="Download all audit logs as CSV file"
            variant="action"
            onClick={() => handleExport('csv')}
          />
          <ActionCard
            icon={FileSpreadsheet}
            title="Export to Excel"
            description="Generate Excel spreadsheet with all data"
            variant="action"
            onClick={() => handleExport('excel')}
          />
          <ActionCard
            icon={Mail}
            title="Email PDF"
            description="Send PDF report via email"
            variant="action"
            onClick={handleEmailPDF}
          />
        </div>

        {!hasSelection ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center border-t pt-4">
            <p className="text-xs text-muted-foreground">
              Select one or more audit records to see additional actions
            </p>
          </div>
        ) : (
          <>
            {/* Export Selected */}
            <div className="space-y-2 border-t pt-3">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
                Export Selected
              </h4>
              <ActionCard
                icon={FileText}
                title="Export Selected to PDF"
                description={`Generate PDF for ${selectedRowIds.size} record${selectedRowIds.size !== 1 ? 's' : ''}`}
                variant="action"
                onClick={() => console.log('Export selected to PDF')}
              />
              <ActionCard
                icon={FileSpreadsheet}
                title="Export Selected to CSV"
                description={`Download ${selectedRowIds.size} record${selectedRowIds.size !== 1 ? 's' : ''} as CSV`}
                variant="action"
                onClick={() => console.log('Export selected to CSV')}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  // Calculate visible columns count for badge
  const visibleColumnsCount = configVisibleColumns.filter(col => col.id !== 'select').length;

  // Define page-specific tabs for the right panel
  const rightPanelTabs: RightPanelTab[] = [
    {
      id: 'actions',
      label: 'Actions',
      icon: Zap,
      content: <CustomActionsTab />,
      showBadge: true,
    },
    {
      id: 'filters',
      label: 'Filters',
      icon: Filter,
      content: <AuditTrailFiltersTab />,
      badgeCount: activeFilterCount,
    },
    {
      id: 'columns',
      label: 'Columns',
      icon: Columns,
      content: <AuditColumnsTab />,
      badgeCount: visibleColumnsCount,
    },
  ];

  return (
    <PageWithRightPanel>
      <div className="flex h-full w-full flex-col overflow-hidden">
        {/* Page Header */}
        <div className="border-b bg-card px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3" data-tour="audit-header">
              <FileText className="h-6 w-6 text-primary" />
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Audit Trail</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Complete history of all system changes and user actions
                </p>
              </div>
            </div>
            
            {/* Search Bar - Right aligned */}
            <div className="flex items-center gap-3">
              <div className="relative w-80">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search audit logs..."
                  value={searchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-tour="search-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Content Container - Fills remaining space */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {(loading || isApplyingFilters) ? (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-4" />
                <p className="text-lg font-medium">Loading audit logs...</p>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-auto">
              <Table data-tour="audit-table">
                <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
                  <TableRow className="border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                    {/* Checkbox column */}
                    <TableHead className="h-12 font-semibold relative" style={{ width: `${columnWidths.select}px` }}>
                      <div className="flex items-center justify-center px-3">
                        <Checkbox
                          checked={paginatedAuditLogs.length > 0 && paginatedAuditLogs.every(log => selectedRowIds.has(log.id))}
                          onCheckedChange={handleSelectAll}
                          data-tour="select-checkbox-input"
                        />
                      </div>
                      {/* Column divider - not resizable for checkbox */}
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 h-6">
                        <div className="w-px h-full bg-slate-300 dark:bg-slate-600" />
                      </div>
                    </TableHead>
                    {visibleColumns.map((column) => {
                      const sortKey = getSortKeyForColumn(column.id);
                      const isRightAligned = false;

                      return (
                        <TableHead 
                          key={column.id} 
                          className={`h-12 font-semibold relative ${isRightAligned ? 'text-right' : ''}`}
                          style={{ width: `${columnWidths[column.id]}px` }}
                        >
                          {sortKey ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="-ml-3 h-8 data-[state=open]:bg-accent hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                              onClick={() => handleSort(sortKey)}
                            >
                              {column.label}
                              <SortIcon columnKey={sortKey} sortConfig={sortConfig} />
                            </Button>
                          ) : (
                            column.label
                          )}
                          {/* Resizable column divider */}
                          <div
                            className="absolute right-0 top-1/2 -translate-y-1/2 h-6 w-1 cursor-col-resize hover:bg-primary/20 group"
                            onMouseDown={(e) => handleMouseDown(column.id, e.clientX)}
                          >
                            <div className="absolute right-0 top-0 bottom-0 w-px bg-slate-300 dark:bg-slate-600 group-hover:bg-primary" />
                          </div>
                        </TableHead>
                      );
                    })}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedAuditLogs.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={visibleColumns.length + 1}
                        className="h-96"
                      >
                        <div className="flex flex-col items-center justify-center space-y-4 py-12">
                          <div className="rounded-full bg-slate-100 dark:bg-slate-800 p-6">
                            <FileX className="h-12 w-12 text-slate-400 dark:text-slate-500" />
                          </div>
                          <div className="space-y-2 text-center">
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">No audit records found</h3>
                            <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                              {searchQuery || activeFilterCount > 0
                                ? "Try adjusting your search or filters to find what you're looking for"
                                : "No audit history available yet"}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedAuditLogs.map((log, index) => (
                      <TableRow 
                        key={log.id}
                        className={`
                          ${index % 2 === 0 ? 'bg-white dark:bg-slate-950' : 'bg-slate-50/50 dark:bg-slate-900/50'}
                          hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors border-b border-slate-100 dark:border-slate-800
                        `}
                      >
                        {/* Checkbox cell */}
                        <TableCell style={{ width: `${columnWidths.select}px` }}>
                          <div className="flex items-center justify-center">
                            <Checkbox
                              checked={selectedRowIds.has(log.id)}
                              onCheckedChange={(checked) => handleSelectionChange(log.id, checked as boolean)}
                            />
                          </div>
                        </TableCell>
                        {visibleColumns.map((column) => renderCellContent(column.id, log))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
          {/* Footer - Inside table card */}
          {!loading && sortedAuditLogs.length > 0 && (
            <div className="border-t bg-card px-6 py-3 flex-shrink-0">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 text-sm text-muted-foreground">
                <div className="flex-shrink-0">
                  Showing {startIndex + 1}-{Math.min(endIndex, sortedAuditLogs.length)} of {sortedAuditLogs.length} entries
                  {sortedAuditLogs.length !== auditLogs.length && ` (filtered from ${auditLogs.length} total)`}
                </div>
                
                {/* Pagination Controls */}
                {totalPagesCalc > 1 && (
                  <div className="flex items-center justify-center gap-2 flex-shrink-0" data-tour="pagination">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setFilters({ ...filters, page: 1 })}
                      disabled={filters.page === 1}
                    >
                      First
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setFilters({ ...filters, page: filters.page! - 1 })}
                      disabled={filters.page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    
                    {/* Page Number Display */}
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-muted-foreground">
                        Page {filters.page} of {totalPagesCalc}
                      </span>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setFilters({ ...filters, page: filters.page! + 1 })}
                      disabled={filters.page === totalPagesCalc}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setFilters({ ...filters, page: totalPagesCalc })}
                      disabled={filters.page === totalPagesCalc}
                    >
                      Last
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Email Dialog */}
      <EmailDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        onSend={handleSendEmail}
        defaultSubject="Audit Trail Report"
        defaultMessage="Please find attached the Audit Trail report."
      />

      {/* Asset Details Modal */}
      {selectedAsset && (
        <AssetEditor
          asset={selectedAsset}
          open={true}
          onClose={handleAssetClose}
          onSave={handleAssetSave}
          mode="view"
        />
      )}

      {/* Right Panel */}
      <RightPanelBase tabs={rightPanelTabs} />

      {/* Product Tour */}
      <ProductTour
        steps={tourSteps}
        isOpen={isTourOpen}
        onClose={closeTour}
        onComplete={completeTour}
      />
    </PageWithRightPanel>
  );
}
